import { Router } from 'preact-router';
import { clearToken, getToken } from './tokenStorage';
import { isApiUrl } from '../constant/apiHosts';
import errorLogger from '../utils/errorLogger';

const axios = require('axios').default;

const BEARER_PREFIX = /^Bearer\s+/i;

/**
 * Reads the `access_token` a request was sent with, from either the URL query
 * string or axios `params`. URLSearchParams decodes, so an encodeURIComponent'd
 * token compares equal to the raw session token.
 * @param {object} config - Axios request config
 * @returns {string|null}
 */
function getRequestAccessToken(config) {
  if (config.params && config.params.access_token) {
    return String(config.params.access_token);
  }
  const url = config.url || '';
  const queryStart = url.indexOf('?');
  if (queryStart < 0) return null;
  try {
    return new URLSearchParams(url.slice(queryStart + 1)).get('access_token');
  } catch {
    return null;
  }
}

/**
 * Reads the Authorization header value (without a "Bearer " prefix), handling
 * both plain-object headers and axios 1.x AxiosHeaders instances.
 * @param {object} config - Axios request config
 * @returns {string|null}
 */
function getRequestAuthorization(config) {
  const headers = config.headers;
  if (!headers) return null;
  let value = null;
  if (typeof headers.get === 'function') {
    value = headers.get('Authorization');
  } else {
    const key = Object.keys(headers).find((h) => h.toLowerCase() === 'authorization');
    value = key ? headers[key] : null;
  }
  return value ? String(value).replace(BEARER_PREFIX, '') : null;
}

/**
 * Checks if an error response indicates the user's SESSION is no longer valid.
 *
 * Only 401/403 from the device API (mdash.net or the Pro compat layer — see
 * constant/apiHosts.js) qualify; responses from backend.kegtron.com are proxy
 * errors (e.g. Square token rejected) and must not clear the session.
 *
 * Most device-API calls authenticate with a DEVICE pubkey, not the session
 * token (`/api/v2/m/device?access_token=<pubkey>`). A 401 there means that one
 * device key is unknown/rotated/unmapped — not that the user is logged out —
 * and since the compat layer returns CORS-readable 401s, treating it as a
 * session failure logged users out of every tab. So:
 *  - credential on the request equals the current session token → auth error
 *  - credential is something else (device pubkey, Basic login creds) → not an
 *    auth error; the caller's own error handling applies
 *  - no credential at all → auth error (pre-existing behaviour, kept: the only
 *    credential-less API calls are session-scoped, and there is no evidence
 *    either way, so fail closed)
 * @param {object} error - Axios error object
 * @returns {boolean} True if the error means the session token was rejected
 */
export function isAuthError(error) {
  if (!error || !error.response || !error.config) return false;
  const status = error.response.status;
  if (status !== 401 && status !== 403) return false;

  if (!isApiUrl(error.config.url || '')) return false;

  const credential = getRequestAccessToken(error.config) || getRequestAuthorization(error.config);
  if (!credential) return true;

  const sessionToken = getToken();
  return Boolean(sessionToken) && credential === sessionToken;
}

// Registered by App (componentDidMount): resets the WHOLE session — token,
// app state, GlobalAccess, caches, polling — via services/sessionReset.js.
// The interceptor is installed in index.js before the App exists, hence a
// registration rather than an import of the app.
let sessionLostHandler = null;

/**
 * Register (or, with null, clear) the handler run when the session token is
 * rejected. Without one, only the token is cleared (pre-boot fallback).
 * @param {Function|null} handler
 */
export function setSessionLostHandler(handler) {
  sessionLostHandler = typeof handler === 'function' ? handler : null;
}

/**
 * Handles a confirmed session-token rejection: ends the session the same way
 * an explicit sign-out does (audit 2026-10-15, M5 — clearing only the token
 * let the next account in the tab see the previous one's sites) and routes to
 * the login page.
 * @param {object} error - Axios error object
 * @returns {Promise} Rejected promise with the original error
 */
function handleAuthError(error) {
  try {
    if (sessionLostHandler) sessionLostHandler();
  } catch (err) {
    // Never let a reset failure keep a rejected token alive; the
    // clearToken() below still ends the session.
    errorLogger.logError('SessionResetFailed', err);
  }
  // Always clear the token, whether or not a handler ran (idempotent).
  clearToken();

  // Redirect to login page
  if (typeof window !== 'undefined') {
    Router.route('/');
  }

  return Promise.reject(error);
}

/**
 * Sets up axios response interceptor to handle authentication errors globally
 * This should be called once at application startup
 * @param {{ onSessionLost?: Function }} [options] - optional session-lost
 *   handler (same as setSessionLostHandler); App registers its own at mount.
 */
export function setupAuthInterceptor({ onSessionLost } = {}) {
  if (onSessionLost) setSessionLostHandler(onSessionLost);
  axios.interceptors.response.use(
    // Success case - pass through unchanged
    (response) => response,

    // Error case - check for auth failures
    (error) => {
      if (isAuthError(error)) {
        return handleAuthError(error);
      }
      // Not an auth error - reject normally
      return Promise.reject(error);
    }
  );
}
